import { useState } from 'react';
import type { ChairRent, ChairRentPeriod, ChairRentReport, Staff } from '@salonomy/types';
import { Button, Card, Field, Pill, Select, Stat, TextInput } from '@salonomy/ui';
import {
  useChairRentReport,
  useChairRents,
  useSetChairRent,
  type EarningsRange,
} from '../../data/commission.js';
import { ApiClientError } from '../../lib/api.js';
import { money, moneyWhole } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';

/** "1400" (pence) → "14.00" for the £ input; null → "". (Mirrors ServiceModal's penceToPounds.) */
function penceToPounds(pence: number | null): string {
  if (pence == null) return '';
  return (pence / 100).toFixed(2);
}

/** "14" | "14.5" | "£14.00" → 1400 pence; null if not a valid positive amount. */
function poundsToPence(input: string): number | null {
  const cleaned = input.replace(/[£,\s]/g, '');
  if (!cleaned) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
}

/**
 * WS9 §4 — chair-rent RECORDING (flat weekly/monthly per renter) feeding the owner's net-position-per-chair
 * report. Recording + reporting ONLY — invoicing renters is out of scope (brief 09 §4, ADR-0009). The net
 * position is COMPUTED by the API (chair rent + retained margin); this surface displays it, never recomputes.
 *
 * Re-recording the same renter replaces the figure (current-state upsert, not a ledger). Style follows the
 * scheduling panels (Card + grid form + settings-list rows).
 */
export function ChairRentPanel({ renters, range }: { renters: Staff[]; range: EarningsRange }) {
  const toast = useToast();
  const { data: rents = [] } = useChairRents();
  const setRent = useSetChairRent();

  const [renterId, setRenterId] = useState('');
  const [amount, setAmount] = useState('');
  const [period, setPeriod] = useState<ChairRentPeriod>('weekly');
  const [note, setNote] = useState('');

  // A staff member's renterId (their sealed book) is what chair rent is recorded against, not their staffId.
  const rentByRenter = new Map(rents.map((r) => [r.renterId, r]));

  function pick(staff: Staff) {
    if (!staff.renterId) return;
    setRenterId(staff.renterId);
    const existing = rentByRenter.get(staff.renterId);
    setAmount(penceToPounds(existing?.amountPence ?? null));
    setPeriod(existing?.period ?? 'weekly');
    setNote(existing?.note ?? '');
  }

  async function record() {
    if (!renterId) {
      toast.error('Pick a renter to record rent for.');
      return;
    }
    const amountPence = poundsToPence(amount);
    if (amountPence == null) {
      toast.error('Enter a rent amount greater than £0.');
      return;
    }
    try {
      await setRent.mutateAsync({ renterId, amountPence, period, note: note.trim() || null });
      toast.success('Chair rent recorded');
    } catch (err) {
      // Surface the API's validation/conflict message faithfully (no silent swallow).
      toast.error(
        err instanceof ApiClientError ? err.message : 'Could not record this chair rent.',
      );
    }
  }

  const renterName = (rent: ChairRent): string =>
    renters.find((s) => s.renterId === rent.renterId)?.displayName ?? 'Renter';

  return (
    <Card>
      <div className="section-title">Chair rent</div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
        A flat weekly or monthly figure per renter, for the owner’s net-position-per-chair view
        below. Recorded for reporting only — renters aren’t invoiced here. Re-recording a renter
        replaces it.
      </div>

      {renters.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0' }}>
          No chair renters on the team yet.
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1fr 1fr auto',
              gap: 12,
              marginTop: 12,
              alignItems: 'end',
            }}
          >
            <Field label="Renter">
              <Select
                value={renterId}
                onChange={(e) => {
                  const staff = renters.find((s) => s.renterId === e.target.value);
                  if (staff) pick(staff);
                  else setRenterId('');
                }}
              >
                <option value="">Select a renter…</option>
                {renters.map((s) => (
                  <option key={s.id} value={s.renterId ?? ''}>
                    {s.displayName}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Rent (£)">
              <TextInput
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </Field>
            <Field label="Per">
              <Select value={period} onChange={(e) => setPeriod(e.target.value as ChairRentPeriod)}>
                <option value="weekly">Week</option>
                <option value="monthly">Month</option>
              </Select>
            </Field>
            <Button onClick={record} disabled={setRent.isPending}>
              Record
            </Button>
          </div>
          <Field label="Note" hint="Optional">
            <TextInput
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. includes backwash"
            />
          </Field>
        </>
      )}

      {rents.length > 0 ? (
        <div className="settings-list" style={{ marginTop: 8 }}>
          {rents.map((rent) => (
            <div key={rent.id} className="settings-row">
              <div>
                <div style={{ fontWeight: 600 }}>{renterName(rent)}</div>
                {rent.note ? (
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {rent.note}
                  </div>
                ) : null}
              </div>
              <Pill tone="neutral">
                {money(rent.amountPence)} / {rent.period === 'weekly' ? 'week' : 'month'}
              </Pill>
            </div>
          ))}
        </div>
      ) : null}

      <NetPositionReport range={range} />
    </Card>
  );
}

/**
 * The owner's net-position-per-chair report (manager-only on the API). Server-computed: chair rent +
 * retained margin per renter over the range. A recorded figure, NOT an invoice.
 */
function NetPositionReport({ range }: { range: EarningsRange }) {
  const enabled = Boolean(range.from && range.to);
  const { data: report, isLoading, isError } = useChairRentReport(range, enabled);

  return (
    <div style={{ marginTop: 20 }}>
      <div className="section-title" style={{ fontSize: 15 }}>
        Net position per chair
      </div>
      {isError ? (
        <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
          Couldn’t load the net-position report for this period.
        </div>
      ) : isLoading || !report ? (
        <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
          Loading…
        </div>
      ) : (
        <NetPositionBody report={report} />
      )}
    </div>
  );
}

function NetPositionBody({ report }: { report: ChairRentReport }) {
  if (report.rows.length === 0) {
    return (
      <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
        No renter activity in this period.
      </div>
    );
  }
  return (
    <>
      <div className="muted" style={{ fontSize: 12.5, margin: '4px 0 12px' }}>
        {moneyWhole(report.totalNetToSalonPence)} to the salon ·{' '}
        {moneyWhole(report.totalChairRentPence)} of that recorded chair rent
      </div>
      <div className="settings-list">
        {report.rows.map((row) => (
          <div key={row.renterId} className="settings-row">
            <div>
              <div style={{ fontWeight: 600 }}>{row.renterName}</div>
              <div className="muted" style={{ fontSize: 12.5 }}>
                {money(row.grossRevenuePence)} revenue · {money(row.commissionPence)} commission ·{' '}
                {money(row.chairRentPence)} rent
                {row.chairRentPeriod ? ` / ${row.chairRentPeriod}` : ''}
              </div>
            </div>
            <Stat label="Net to salon" value={money(row.netToSalonPence)} />
          </div>
        ))}
      </div>
    </>
  );
}
